export default {
    units: {
        szt: 'pcs',
        opak: 'pack',
        peczek: 'bunch',
    },
    noCategory: 'No category',
    firstSteps: {
        start: {
            title: 'Quick start',
            action: 'Set up',
            what: 'Tick the shops you go to (Lidl, Boots, IKEA…) and the ready-made templates.',
            why: 'Shops get their own aisles and aisle order, and shop-specific lists and templates set themselves up — no need to configure everything one by one.',
        },
        quantity: {
            title: 'Item with a quantity',
            action: 'Show me how',
            what: 'Type e.g. "2 l milk" or "eggs 10 pcs" — the quantity and unit are recognised automatically.',
            why: 'Adding the same thing again increases the quantity instead of duplicating the item, and in the shop you can see at a glance how much to take.',
        },
        dictation: {
            title: 'Dictate a list',
            action: 'Dictate',
            what: 'Say or paste the whole list at once: "two litres of milk, bread, half a kilo of cheese".',
            why: 'Fastest for a longer list — you\'ll see and fix every item before it goes on the list.',
        },
        ingredients: {
            title: 'Ingredients from a meal',
            action: 'Choose a meal',
            what: 'Choose a meal and the number of servings — we\'ll add its ingredients, summed up with other meals.',
            why: 'You cook from your plan without copying out the recipe. Untick what you already have at home, and each item shows what it\'s for.',
        },
        list: {
            title: 'A second list',
            action: 'New list',
            what: 'A separate list for an occasion: "BBQ", "Party", "Renovation" — not for a shop, because every list works in every shop.',
            why: 'Everyday shopping doesn\'t get mixed up with bigger plans. You can move an item between lists in one go.',
        },
        template: {
            title: 'Template',
            action: 'New template',
            what: 'A set of things you buy regularly — e.g. "Weekly".',
            why: 'Set it up once, then add it to a list with one tap — leaving out what you still have at home.',
        },
        kind: {
            title: 'Shop type',
            action: 'Create a type',
            what: '"Grocery" for Lidl and Tesco, "Chemist" for Boots — from a preset, the aisles are picked for you.',
            why: 'In Shopping mode you won\'t see bread at the chemist or shampoo at Lidl — they go to "Elsewhere" with a hint on where to buy them.',
        },
        layout: {
            title: 'Aisles in your shop',
            action: 'Arrange aisles',
            what: 'The order of aisles in the shop you go to most often — every shop can have its own.',
            why: 'In Shopping mode you walk through the shop once, from the entrance to the till: at Lidl starting with veg, at Tesco with the fridges.',
        },
        trip: {
            title: 'First shopping trip',
            action: 'Go shopping',
            what: 'Shopping mode: tick off what you put in your basket, and at the end tap "Finish shopping".',
            why: 'Bought items go to History — that\'s where the latest prices and "Buy often" come from. Works without signal too.',
        },
        expense: {
            title: 'Expense in Wallet',
            action: 'Show me how',
            what: 'After shopping, save an expense in Wallet — the amount is suggested from the item prices.',
            why: 'Your shopping is deducted from "To spend" straight away, without copying out the receipt.',
        },
    },
    help: {
        add: {
            title: 'Adding',
            text: 'Type what to buy — we understand "2 l milk", "eggs 10 pcs" and several things separated by commas. We suggest what you\'ve bought before, with the latest price.',
            more: 'Adding the same thing again (same unit) increases the quantity instead of duplicating the item. The shop category is suggested automatically — the same as last time for that name. Below the field: dictate the whole list, add a meal\'s ingredients, pick from "Buy often" or from a template.',
        },
        shortcuts: {
            title: 'Faster than typing',
            text: 'Dictate the whole list at once, add a meal\'s ingredients, pick from "Buy often" or from a template. You always see a preview first.',
        },
        list: {
            title: 'List',
            text: 'Items in aisle order, urgent ones at the top. The circle ticks an item off, tapping the name opens details: quantity, price, substitute, "only at" a shop.',
            more: 'The switch above the list changes the layout to the order items were added. "Out of stock" stays on the list with a date, so you remember where to look.',
        },
        inBasket: {
            title: 'In the basket',
            text: 'Ticked-off items. "Finish shopping" moves them to History, and the rest waits for next time.',
        },
        quickStart: {
            title: 'Quick start',
            text: 'Sets up Groceries from ready-made presets: tick the shops you go to, shop-specific lists and templates — shop types, their aisles and aisle order are created automatically.',
            more: 'Nothing gets duplicated: a shop, type, category or list with a name you already have stays unchanged. You can run it again to add more shops.',
        },
        qsStores: {
            title: 'Where you shop',
            text: 'Each ticked shop gets its type — Grocery, Chemist, Sports… — with aisles in the usual order. That way, in Shopping mode you only see what you can actually buy there.',
            more: 'Lidl and Tesco get the same "Grocery" type, so changing the aisles of the type works in both. You can arrange the aisle order for a specific shop later in the Shops tab.',
        },
        qsLists: {
            title: 'Shop-specific lists',
            text: 'The "Sport · Decathlon" list is for one shop: you\'ll only see its items in Decathlon\'s run-through, not when shopping at Lidl. The list appears when you tick its shop.',
            more: 'You always have "Current" — a list for all shops, for everyday shopping. You can change any list\'s shop later when editing it.',
        },
        qsTemplates: {
            title: 'Templates',
            text: 'Ready-made sets of items with categories, e.g. "Weekly" or "BBQ". On the list, choose "From a template", untick what you have at home, and the rest goes on the list.',
            more: 'You can change or delete a template in the "Lists and templates" tab.',
        },
        trip: {
            title: 'Go shopping',
            text: 'Choose where you are — and you get that shop\'s run-through: everything you need to buy there, from all lists at once, in the order of its aisles. Big tap targets are easy to tick, and it works without signal too.',
            more: 'From this shop\'s lists it takes everything; from lists without a shop (like "Current") whatever you can buy there. Items from other shops\' lists that are also sold here wait in the collapsed "From other lists". "Finish shopping" turns it all into one shopping trip in History.',
        },
        lists: {
            title: 'Lists',
            text: 'A list says what to buy — a separate one per occasion: "Current", "BBQ", "Renovation". Usually it\'s for all shops; you make a list for one shop (e.g. "Sport · Decathlon") by choosing its shop. The default list is where everything added on the go ends up.',
            more: 'After shopping you can archive a list — it comes back with one tap.',
        },
        listVsStore: {
            title: 'List vs shop',
            text: 'On the left, the list — what to buy. On the right, the shop as a route from the entrance to the till through its aisles, with your items in their aisles. Switch shop: the same list is arranged differently, and whatever you can\'t buy there (shampoo at Lidl) waits in "Elsewhere" with a hint on where to get it.',
            more: 'A list can have its own shop — "Sport" for Decathlon. Then you\'ll only see its items at Decathlon, while "Current" (no shop) shows up in every shop where you can buy them.',
        },
        templates: {
            title: 'Templates',
            text: 'Ready-made sets, e.g. "Weekly" or "Party". "Use" shows the items so you can untick what you have at home, and adds the rest to the list.',
        },
        archive: {
            title: 'Archive',
            text: 'Lists put away after shopping. You can restore them or delete them permanently.',
        },
        frequent: {
            title: 'Buy often',
            text: 'What you\'ve bought most often in recent months. A tap adds it to the default list with the quantity from last time.',
        },
        history: {
            title: 'Shopping trips',
            text: 'Every finished shopping trip: where, from which list, what was bought and for how much. From here you can also save an expense in Wallet if it wasn\'t saved straight away.',
            more: 'History is where the latest prices, "Buy often" and category suggestions come from.',
        },
        stores: {
            title: 'Shops',
            text: 'A shop says where you buy. "I\'m here" shows everything you need to buy there. Every shop has a type and its own aisle order. The type says which aisles it has (Lidl — grocery, Boots — chemist). "Aisles" puts them in the order you pass them: at Lidl veg first, at Tesco the fridges.',
            more: 'Without its own order, a shop uses the order of its type, and without a type — all categories in the general order.',
        },
        kinds: {
            title: 'Shop types',
            text: 'A type is the set of aisles a shop like that has: "Grocery" (veg, bread, dairy…) for Lidl and Tesco, "Chemist" (cosmetics, toiletries…) for Boots. You set it up once and attach it to many shops.',
            more: 'An aisle outside the type doesn\'t exist in that shop — in Shopping mode its items wait in "Elsewhere". Adding an aisle to a type works straight away in all its shops.',
        },
        defaultList: {
            title: 'Default list',
            text: 'Whatever you add without choosing a list goes here — dictation, "Buy often", the button in the top bar.',
        },
        oldList: {
            title: 'Old shopping list',
            text: 'You can copy the items from the old list once — we\'ll split meals into ingredients. The old list stays unchanged.',
        },
        categories: {
            title: 'Shop categories',
            text: 'Shop aisles (Dairy, Bakery, Cosmetics…). An item has one category in all shops — milk is Dairy at both Lidl and Tesco. Shared with the old Shopping list.',
            more: 'The order here applies in shops without a type. A new category is remembered for the name: next time "milk" goes to Dairy by itself.',
        },
    },
    tour: {
        welcome: {
            title: 'Welcome to Groceries',
            text: 'Shopping lists that arrange themselves in aisle order, remember prices and work in the shop without signal. In a minute I\'ll show you what\'s where.',
        },
        done: {
            title: 'That\'s it',
            text: 'The guide is always available under the "Help" button — Getting started is there too. And "What\'s this?" next to each section explains it without the guide.',
        },
    },
    quickStartDone: {
        stores: '{n} shop | {n} shops',
        kinds: '{n} shop type | {n} shop types',
        categories: '{n} category | {n} categories',
        lists: '{n} list | {n} lists',
        templates: '{n} template | {n} templates',
        created: 'Created: {parts}.',
        nothing: 'Everything was already there — nothing needed creating.',
    },
    list: {
        moduleTitle: 'Groceries',
        legacyBanner: 'You have {n} thing on the old shopping list — move it to Groceries. | You have {n} things on the old shopping list — move them to Groceries.',
        legacyMove: 'Move',
        legacyNo: 'No, thanks',
        subtitleTemplate: 'template · {n} item | template · {n} items',
        subtitleStore: 'list for {store}',
        subtitleToBuy: '{n} to buy',
        subtitleCost: 'approx. {amount}',
        mergedNow: '{name}: now {quantity}',
        alreadyOnList: '{name} is already on the list',
        mergedSummary: 'Added: {added}, merged with what was already there: {merged}.',
        addFailed: 'Couldn\'t add.',
        saveFailedConnection: 'Couldn\'t save — check your connection.',
        fromTemplateTitle: 'From template “{name}”',
        useTemplateTitle: 'From template “{name}”',
        uncheckAtHome: 'Untick what you have at home.',
        addedElsewhere: 'Added: {count} — including {lists}.',
        addedTo: 'Added to “{name}”: {count}.',
        addedToMerged: 'Added to “{name}”: {count} ({merged} merged with what was there).',
        deleted: 'Deleted.',
        tripFinished: 'Shopping finished: {n} item in History. | Shopping finished: {n} items in History.',
        expenseSaved: 'Expense saved in Wallet.',
        finishFailed: 'Couldn\'t finish shopping.',
        form: {
            name: 'Name',
            namePlaceholder: 'e.g. BBQ',
            store: 'Shop for this list',
            storeHint: 'A list for one shop (e.g. Sport → Decathlon): you\'ll only see its items there. Empty — a list for all shops, like “Current”.',
            isDefault: 'Default',
            isDefaultHint: 'Everything added on the go ends up here',
            templateName: '{name} — template',
        },
        listSaved: 'List saved.',
        listCreated: 'New list “{name}”.',
        templateSaved: 'Template “{name}” saved ({n} items).',
        saveFailed: 'Couldn\'t save.',
        allUnchecked: 'Everything unticked.',
        quantityHint: {
            title: 'Item with a quantity',
            text: 'Type e.g. “2 l milk” or “eggs 10 pcs” and press Enter. Separate several things with commas.',
        },
        listPlaceholder: 'List',
        more: 'More',
        menu: {
            edit: 'Edit list',
            new: 'New list',
            saveAsTemplate: 'Save as template',
            uncheckAll: 'Untick all',
            allLists: 'All lists',
        },
        goShopping: 'Go shopping',
        useTemplate: 'Use template',
        templateItemsTitle: 'TEMPLATE ITEMS',
        addTitle: 'ADD',
        quickStartCard: {
            title: 'Quick start — set up Groceries in a minute',
            text: 'Tick the shops you go to and the ready-made templates — aisles, aisle order and lists will set themselves up.',
            button: 'Let\'s go',
        },
        templateTitle: 'TEMPLATE',
        toBuyTitle: 'TO BUY',
        layout: {
            label: 'List layout',
            shelf: 'Aisles',
            added: 'Order added',
        },
        emptyTemplate: 'Add the items that should go on the list with this template.',
        empty: 'The list is empty. Type something above — e.g. “milk, bread, 10 eggs”.',
        allInBasket: 'Everything\'s in the basket.',
        urgentTitle: 'URGENT',
        estimate: 'Approx. {total} — from item prices or the last prices paid.',
        estimateMissing: 'Approx. {total} · no price: {missing} — from item prices or the last prices paid.',
        inBasketTitle: 'IN THE BASKET · {n}',
        finish: 'Finish shopping',
        fromDictation: 'From dictation',
        fromDictationHint: 'Check, correct and add.',
        ingredients: 'Ingredients',
        ingredientsFor: 'for: {names}',
        dialog: {
            edit: 'List',
            new: 'New list',
            template: 'Save as template',
        },
        undo: 'Undo',
    },
    history: {
        title: 'History',
        thisMonth: '{amount} this month',
        addedToDefault: '“{name}” is on the “{list}” list.',
        addFailed: 'Couldn\'t add.',
        expenseSaved: 'Expense saved in Wallet.',
        expenseHint: {
            title: 'Expense in Wallet',
            text: 'After “Finish shopping” we\'ll suggest an expense straight away, with the amount from item prices. Here you can save it later: “Save in Wallet” next to the shopping trip.',
        },
        frequentTitle: 'BUY OFTEN',
        boughtTimes: 'Bought {n}× in recent months',
        frequentEmpty: 'Appears after your first shopping trips finished in Shopping mode.',
        tripsTitle: 'SHOPPING TRIPS',
        itemCount: '{n} item | {n} items',
        pricedOf: 'prices for {priced} of {count}',
        expenseSavedTitle: 'Expense saved in Wallet',
        wallet: 'Wallet',
        saveInWallet: 'Save in Wallet',
        remove: 'Remove from History',
        empty: 'Shopping trips land here after “Finish shopping” — with what was bought, where and for how much.',
        removeConfirm: {
            title: 'Remove from History?',
            message: 'The shopping trip will disappear from History and from “Buy often”. The expense in Wallet stays.',
        },
    },
    settings: {
        title: 'Groceries settings',
        defaultSet: 'Default list: “{name}”.',
        legacyEmpty: 'The old Shopping list is empty.',
        legacyFailed: 'Couldn\'t load the old list.',
        copied: 'Copied: {count}. The old list stays unchanged.',
        defaultTitle: 'DEFAULT LIST',
        topBar: 'The Groceries button in the top bar opens it too — {link}.',
        topBarLink: 'turn it on in the top bar settings',
        legacyTitle: 'OLD SHOPPING LIST',
        legacyText: 'Copy what you have on the old list — you\'ll see a preview, and the old list stays unchanged.',
        legacyButton: 'Copy from the old list',
        storesTitle: 'SHOPS, TYPES AND CATEGORIES',
        storesText: 'They now have their own tab — {link}.',
        storesLink: 'Shops',
        legacyProposalsTitle: 'From the old list',
        legacyProposalsSubtitle: 'Meals are split into ingredients. Untick what you don\'t need.',
    },
    lists: {
        title: 'Lists and templates',
        listCount: '{n} list | {n} lists',
        newList: 'New list',
        itemCount: '{n} item | {n} items',
        inBasket: '{n} in the basket',
        templatePlaceholder: 'e.g. Weekly',
        saved: 'Saved.',
        archived: '“{name}” archived.',
        restored: '“{name}” restored.',
        defaultCannotArchive: 'The default list can\'t be archived — set another list as default first.',
        failed: 'Something went wrong.',
        nowDefault: '“{name}” is now the default.',
        deleteFailed: 'Couldn\'t delete.',
        listsTitle: 'LISTS',
        addList: '+ New list',
        defaultBadge: 'default',
        edit: 'Edit',
        makeDefault: 'Make default',
        toArchive: 'Archive',
        templatesTitle: 'TEMPLATES',
        addTemplate: '+ New template',
        templateItems: '{n} item · edit | {n} items · edit',
        use: 'Use',
        deleteTemplate: 'Delete template {name}',
        templatesEmpty: 'A set of things you buy regularly — e.g. “Weekly”. You can also save an existing list as a template (the “⋯” menu on the list).',
        archiveTitle: 'ARCHIVE · {n}',
        restore: 'Restore',
        delete: 'Delete',
        archiveEmpty: 'Empty.',
        dialog: {
            template: 'Template',
            list: 'List',
            newTemplate: 'New template',
            newList: 'New list',
        },
        deleteConfirm: 'The items on the list will disappear. Shopping history stays.',
    },
    stores: {
        title: 'Shops',
        status: {
            ownWithKind: '{kind} · own order',
            own: 'own order',
            kind: '{kind} · type order',
            none: 'no type · all categories',
        },
        kindSet: '{store}: {kind}.',
        kindCleared: '{store}: no type — all categories.',
        kindSaveFailed: 'Couldn\'t save the shop type.',
        shelvesSaved: '{store}: aisle order saved.',
        orderSaveFailed: 'Couldn\'t save the order.',
        backToKindOrder: '{store}: back to the “{kind}” type order.',
        backToGeneralOrder: '{store}: back to the general order.',
        addStoreFailed: 'Couldn\'t add the shop.',
        presets: {
            grocery: { name: 'Grocery', hint: 'Lidl, Tesco — veg, bread, dairy…' },
            drugstore: { name: 'Chemist', hint: 'Boots, Superdrug — cosmetics, toiletries, cleaning…' },
            sport: { name: 'Sports', hint: 'Decathlon, JD Sports — clothing, shoes, equipment…' },
            home: { name: 'Home and furniture', hint: 'IKEA, Dunelm — furniture, kitchen, textiles…' },
            diy: { name: 'DIY', hint: 'B&Q, Wickes — tools, paint…' },
            empty: { name: 'Empty', hint: 'You choose the categories yourself' },
        },
        kindExists: 'The “{name}” type already exists — you can adjust it here.',
        kindCreated: 'The “{name}” type with {n} category — attach shops to it. | The “{name}” type with {n} categories — attach shops to it.',
        kindCreateFailed: 'Couldn\'t create the shop type.',
        kindSaved: 'Shop type saved.',
        generalOrderSaved: 'General order saved.',
        kindHint: {
            title: 'Shop type',
            text: 'Create “Grocery” (for Lidl, Tesco) or “Chemist” (for Boots) from a preset — the categories are picked for you. Then choose each shop\'s type next to it.',
        },
        layoutHint: {
            title: 'Aisles in your shop',
            text: 'Next to a shop, tap “Aisles” and drag them into the order you pass them — from the entrance to the till.',
            textNoStores: 'First add a shop, then tap “Aisles” next to it and drag them into the order you pass them.',
        },
        quickStart: 'Quick start',
        storesTitle: 'SHOPS',
        storesIntro: 'Choose a type for each shop — it says which aisles the shop has. “Aisles” sets the order of aisles in that one shop.',
        newCategories: 'new: {names} — set their place',
        noKindOption: '— no type —',
        kindPlaceholder: 'Shop type',
        shelves: 'Aisles',
        imHereTitle: '{store} run-through: everything to buy there, from all lists',
        imHere: 'I\'m here',
        orderOwn: 'This shop\'s own order — drag or use the arrows.',
        orderKind: 'Currently the “{kind}” type order. Move an aisle and this shop gets its own.',
        orderGeneral: 'Currently the general order. Move an aisle and the shop gets its own.',
        kindNoCategories: 'This type has no categories yet — tick them in “Shop types”.',
        newBadge: 'new',
        moveUp: '{name}: up',
        moveDown: '{name}: down',
        resetToKind: 'Back to the “{kind}” type order',
        resetToGeneral: 'Back to the general order',
        storesEmpty: 'Add the shops you go to — e.g. Lidl, Tesco, Boots.',
        newStorePlaceholder: 'New shop, e.g. Boots',
        newStoreLabel: 'New shop name',
        add: 'Add',
        kindsTitle: 'SHOP TYPES',
        newKind: '+ New type',
        kindsIntro: 'A type says which aisles a shop like that has. Lidl and Tesco are the same type, Boots a different one. What isn\'t in the type won\'t show in that shop in Shopping mode — it goes to “Elsewhere”.',
        kindNoStores: 'no shops — attach them above',
        deleteKind: 'Delete type {name}',
        kindAisles: '{n} aisle: {names} | {n} aisles: {names}',
        kindsEmpty: 'Without types every shop has all categories. Start with the “Grocery” or “Chemist” preset.',
        categoriesTitle: 'CATEGORIES · {n}',
        categoriesIntro: 'Shop aisles, shared by all shops and the old list. The order here is the order in shops without a type.',
        renameLabel: 'New name: {name}',
        rename: 'Rename',
        deleteCategory: 'Delete category {name}',
        newCategoryPlaceholder: 'New category, e.g. Cosmetics',
        newCategoryLabel: 'New category name',
        deleteKindConfirm: 'Shops of this type will have all categories. Their own order stays.',
        deleteCategoryConfirm: 'Items and products in this category will be left without one. It will also disappear from the old Shopping list.',
    },
    addBar: {
        placeholder: 'Add, e.g. 2 l milk, bread, eggs 10 pcs',
        label: 'Add items',
        addMany: 'Add {n}',
        add: 'Add',
        boughtTimes: 'bought {n}×',
        fromCatalogue: 'from the catalogue',
        wasOnList: 'was on the list',
        dictate: 'Dictate',
        fromMeal: 'From a meal',
        frequent: 'Buy often',
        fromTemplate: 'From a template',
    },
    dictation: {
        unavailable: 'Dictation is unavailable right now.',
        recognisingItems: 'Recognising items…',
        nothingFound: 'I couldn\'t find anything to buy — say e.g. “milk, bread, 10 eggs”.',
        recogniseFailed: 'Couldn\'t recognise the list.',
        noMic: 'No access to the microphone.',
        silent: 'I can\'t hear anything — check your microphone.',
        recognisingSpeech: 'Recognising speech…',
        notRecognised: 'Speech not recognised — try again.',
        uploadFailed: 'Couldn\'t send the recording.',
        title: 'Dictate a list',
        close: 'Close',
        intro: 'Say or paste everything at once — e.g. “two litres of lactose-free milk, bread, half a kilo of cheese”. You\'ll see a preview before adding.',
        stop: 'Stop recording',
        record: 'Record',
        orType: 'Or type / paste',
        placeholder: 'milk, bread, butter, 10 eggs, something for the BBQ',
        recognise: 'Recognise',
    },
    item: {
        title: 'Item',
        name: 'Name',
        quantity: 'Quantity',
        unit: 'Unit',
        note: 'Note',
        notePlaceholder: 'e.g. lactose-free, large',
        substitute: 'Substitute',
        substitutePlaceholder: 'if there\'s none, then…',
        price: 'Price',
        lastPrice: 'Last time: {price}',
        priceHint: 'For the whole item',
        category: 'Category',
        onlyStore: 'Only at',
        onlyStoreHint: 'In any other shop it goes to “Elsewhere”.',
        forWhat: 'What it\'s for',
        forWhatPlaceholder: 'e.g. Ratatouille',
        urgent: 'Urgent',
        urgentHint: 'At the top of the list and in Shopping mode',
        moveTo: 'Move to list',
        copyTo: 'Also copy to',
        moved: 'Moved to “{name}”.',
        copied: 'Copied to “{name}”.',
        saved: 'Saved.',
        saveFailed: 'Couldn\'t save.',
        deleteFailed: 'Couldn\'t delete.',
    },
    row: {
        substitute: 'substitute: {name}',
        forWhat: 'for: {name}',
        onlyAt: 'only at: {name}',
        uncheck: '{name}: untick',
        check: '{name}: in the basket',
        urgent: 'Urgent',
        urgentLabel: 'urgent',
        missing: 'out of stock',
        missingOn: 'out of stock {day}',
        pending: 'Waiting to be sent',
        pendingLabel: 'waiting to be sent',
        price: 'Price',
        lastPaid: 'Last paid',
        missingButton: 'Out of stock',
        details: '{name}: details',
    },
    kind: {
        title: 'Shop type',
        newTitle: 'New shop type',
        close: 'Close',
        intro: 'Tick the aisles a shop like this has and put them in the usual order. Each shop of this type can then have its own.',
        name: 'Name',
        namePlaceholder: 'e.g. Grocery, Chemist, DIY',
        noCategories: 'You don\'t have any categories yet — add them in the “Categories” section.',
        count: 'In this type: {count} of {total}',
        saving: 'Saving…',
        save: 'Save',
    },
    howItWorks: {
        title: 'HOW IT WORKS',
        lead: 'The {list} says what to buy. The {store} — where you are and how to walk through the aisles.',
        leadList: 'list',
        leadStore: 'shop',
        listTitle: 'LIST · WHAT TO BUY',
        exampleBadge: 'example',
        listFallback: 'List',
        example: {
            veg: 'Veg',
            bread: 'Bakery',
            dairy: 'Dairy',
            cosmetics: 'Cosmetics',
            hygiene: 'Toiletries',
            milk: 'milk',
            breadItem: 'bread',
            carrots: 'carrots',
            shampoo: 'shampoo',
            store1: 'Lidl',
            store2: 'Tesco',
            store3: 'Boots',
            grocery: 'Grocery',
            drugstore: 'Chemist',
            list: 'Current',
        },
        more: '+ {n} more',
        listNote: 'It isn\'t tied to a shop — it can hold things from several shops at once.',
        storeTabs: 'Shop in the diagram',
        storeTitle: 'SHOP · WHERE AND HOW TO WALK',
        summaryKind: '{store}: {n} aisle from the “{kind}” type | {store}: {n} aisles from the “{kind}” type',
        summaryNoKind: '{store}: {n} aisle — no type, all of them | {store}: {n} aisles — no type, all of them',
        orderOwn: 'in this shop\'s order',
        orderKind: 'in the type\'s order',
        orderGeneral: 'in the general order',
        switchStore: 'Switch shop — the same list will be arranged differently.',
        pathLabel: 'Aisle order',
        entrance: 'Entrance',
        passing: 'you pass',
        looseHint: 'in every shop, at the end',
        till: 'Till',
        awayTitle: 'Elsewhere — you can\'t buy these in this shop',
        buyAt: 'buy at: {stores}',
        notInKind: 'the {aisle} aisle isn\'t in this shop\'s type',
        addToList: 'Add something to the list to see it here →',
        addStores: 'Add your shops →',
        arrange: 'Arrange this shop\'s aisles',
        changeKind: 'Change type',
        chooseKind: 'Choose a type',
        picked: {
            inKind: 'it\'s in the “{kind}” type',
            noKind: 'a shop without a type has all aisles',
            orderOwn: 'its place comes from this shop\'s own order',
            orderKind: 'its place comes from the type\'s order',
            orderGeneral: 'its place comes from the general order',
            fromList: 'From the list: {items}.',
            nothing: 'Nothing from this list — you\'ll walk past.',
            text: '{name}: {where}, {order}. {fromList}',
        },
    },
    mealPicker: {
        loadFailed: 'Couldn\'t load meals.',
        noIngredients: 'These meals have no ingredients from the catalogue.',
        countFailed: 'Couldn\'t work out the ingredients.',
        title: 'Ingredients from a meal',
        intro: 'Tick what you\'re cooking and for how many servings. Ingredients from several meals are added up.',
        searchPlaceholder: 'Search for a meal or semi-product…',
        searchLabel: 'Search for a meal',
        loading: 'Loading…',
        empty: 'You don\'t have any meals yet. You can add them in the Food module.',
        meal: 'meal',
        semiProduct: 'semi-product',
        fewer: '{name}: fewer servings',
        portions: '{n} serv.',
        more: '{name}: more servings',
        chosen: 'Selected: {n}',
        noneChosen: 'Nothing selected',
        counting: 'Calculating…',
        show: 'Show ingredients',
    },
    pick: {
        frequentTitle: 'Buy often',
        templateTitle: 'From a template',
        frequentHint: 'Tap to add — with the quantity from last time.',
        templateHint: 'Choose a template, then untick what you have at home.',
        frequentEmpty: 'What you buy most often will appear here — after your first trips in Shopping mode.',
        templatesEmpty: 'You don\'t have any templates yet. Create them in the “Lists and templates” tab — or save a list as a template.',
        itemsShort: '{n} items',
    },
    proposals: {
        defaultTitle: 'Add to list',
        elsewhere: '{n} to “{list}”',
        addFailed: 'Couldn\'t add — try again.',
        uncheckAll: 'Untick all',
        checkAll: 'Tick all',
        uncheckHint: 'Untick what you have at home',
        addItem: 'Add: {name}',
        heard: '“{text}”',
        noCategory: 'no category',
        toList: 'to list {list}',
        edit: 'Edit: {name}',
        unitShort: 'Unit',
        nothing: 'Nothing to add.',
        restTo: 'The rest to',
        to: 'To list',
        chooseList: 'Choose a list',
        adding: 'Adding…',
    },
    quickStart: {
        loadFailed: 'Couldn\'t load the ready-made presets.',
        runFailed: 'Couldn\'t set things up — try again.',
        kindHint: {
            grocery: 'veg, bread, dairy, meat…',
            drugstore: 'cosmetics, toiletries, cleaning…',
            sport: 'clothing, shoes, equipment, bikes…',
            home: 'furniture, kitchen, textiles, lighting…',
            diy: 'tools, paint, electrics, garden…',
        },
        title: 'Quick start',
        intro: 'Tick where you shop and what you need — shops get their own aisles and order, and lists and templates set themselves up. What you already have stays unchanged.',
        storesTitle: '1. WHERE YOU SHOP',
        alreadyHave: 'already added',
        listsTitle: '2. SHOP-SPECIFIC LISTS',
        listsIntro: 'You always have “Current” — for everyday shopping in any shop. These lists are for one shop: you\'ll only see their items there.',
        thingsFor: 'things for {store}',
        listsEmpty: 'Tick Decathlon, IKEA or Leroy Merlin to get a list for that shop.',
        templatesTitle: '3. TEMPLATES',
        templatesIntro: 'Ready-made sets — add them to a list with one tap, leaving out what you have at home.',
        running: 'Setting up…',
        run: 'Set up',
    },
    walletExpense: {
        enterAmount: 'Enter an amount.',
        note: 'Shopping: {list}',
        saveFailed: 'Couldn\'t save the expense.',
        title: 'Expense in Wallet',
        partial: 'Amount from {priced} of {count} items — correct it from the receipt.',
        loading: 'Loading Wallet…',
        noWallet: 'Wallet isn\'t set up yet. {link}, and then you can save the expense from your shopping history.',
        noWalletLink: 'Set up Wallet',
        amount: 'Amount',
        account: 'Account',
        category: 'Category',
        fromReceipt: 'From a receipt',
        save: 'Save {amount}',
    },
    shopping: {
        offlineListOnly: 'No signal — showing only this list.',
        loadFailed: 'Couldn\'t load the shop\'s run-through.',
        goShopping: 'Go shopping',
        fallbackTitle: 'Shopping',
        noStoreSubtitle: 'no shop — just this list',
        fromLists: 'from list: {names} | from lists: {names}',
        nothingHere: 'nothing to buy here',
        scanUnknown: 'I don\'t know this code — tick it off by hand.',
        scanChecked: 'Ticked off: {name}',
        scanNotOnList: '{name} — it\'s not on the list.',
        scanFailed: 'Couldn\'t check the code.',
        finishOffline: 'No signal — your ticks are waiting on the phone. You can finish when you\'re back online.',
        finishFailed: 'Couldn\'t finish shopping — try again.',
        label: 'Shopping mode',
        close: 'Close Shopping mode',
        changeStore: 'Change shop',
        chooseStore: 'Choose a shop',
        scan: 'Scan product barcode',
        offlinePending: 'No signal · {n} waiting',
        sending: 'Sending…',
        progress: '{done} of {total}',
        whereAreYou: 'Where are you?',
        whereHint: 'You\'ll see everything you need to buy there, from all lists at once — in that shop\'s aisle order.',
        listStore: 'list\'s shop',
        nothing: 'nothing',
        noStoreOption: 'No shop — just the “{name}” list',
        loading: 'Putting together the shop\'s run-through…',
        emptyStore: 'Nothing to buy here — add something below or change shop.',
        emptyList: 'The list is empty — add something below.',
        substitute: 'Substitute:',
        price: 'Price: {price}',
        lastPrice: 'Last time: {price}',
        removeFromList: 'Remove from list',
        missingTitle: 'OUT OF STOCK · STAYS ON THE LIST',
        elsewhereTitle: 'Elsewhere · {n}',
        elsewhereHint: 'You can\'t buy these in this shop — an aisle outside its type or “only at” another shop.',
        otherTitle: 'From other lists, also available here · {n}',
        otherHint: 'They\'re on other shops\' lists — you can buy them right away.',
        addPlaceholder: 'Add, e.g. 2 l milk',
        addLabel: 'Add an item',
        finishing: 'Finishing…',
    },
};
